/**
 * FC.01 — Tipos e interfaces cotejo completitud PTEL
 * La criticidad es criterio operativo de PTEL Tool, no cita de norma (NORMA-b).
 *
 * Contrato canónico consumido por:
 *   services/cotejo/*  ·  components/wizard-v2/step3-coteja/*
 */

import type { InfrastructureType } from '../../types/infrastructure';
import type { ProcessedInfrastructure } from '../../types/processing';

// ─── Enumeraciones ───────────────────────────────────────────────────────────

/** Criticidad legal de la tipología según normativa aplicable */
export type CriticidadLegal = 'critica' | 'alta' | 'media' | 'baja';

/** Estado del elemento tras el cotejo */
export type EstadoCotejo =
  | 'incluido'            // Match confirmado (score ≥ umbral confirmado)
  | 'probable'            // Match probable  (score entre umbrales)
  | 'ausente_critico'     // En referencia, ausente en PTEL, criticidad crítica/alta
  | 'ausente_recomendado' // En referencia, ausente en PTEL, criticidad media/baja
  | 'sobrante'            // En PTEL sin candidato de referencia (puede ser válido)
  | 'sin_match';          // Score insuficiente para determinar

/** Fuentes de referencia disponibles en R2 */
export type FuenteReferencia =
  | 'DERA_G12'
  | 'DERA_G10'
  | 'DERA_G3'
  | 'Catastro'
  | 'NGA'
  | 'AGOL'
  | 'IAPH'
  | 'EduCenters'
  | 'GCFacilities'
  | 'OSMSecurity';

// ─── Metadatos enriquecidos ───────────────────────────────────────────────────

/** Metadatos opcionales de capacidad/aforo adjuntos a un candidato */
export interface MetadatosEnriquecidos {
  aforo?: number;
  aforoUnidad?: string;       // 'camas' | 'alumnos' | 'personas/día' | 'personas' | …
  superficieM2?: number;
  capacidadM3?: number;       // Hidráulica (DERA G3)
  tensionKV?: number;         // Energía
  potenciaMW?: number;
  esBIC?: boolean;            // Patrimonio (IAPH)
  codigoCentro?: string;      // Educación

  // ── Hidrología + SAIH ───────────────────────────────────────────────────
  // FC.06e.bis — campos hidráulicos ampliados
  /** Cota del embalse en metros sobre el nivel del mar */
  cotaEmbalseM?: number;
  /** Uso principal: 'abastecimiento' | 'riego' | 'laminacion' | 'mixto' */
  usoHidraulico?: string;
  // F7.1 — REDIAM dato diario 08:00 h
  /** Volumen embalsado actual (hm³) — REDIAM */
  volumenEmbalsadoHm3?: number;
  /** Porcentaje de llenado actual [0-100] — REDIAM */
  porcentajeLlenado?: number;
  /** Fecha del dato hidrológico (ISO 8601) — REDIAM / SAIH */
  fechaDatoHidro?: string;
  /** Nivel de cobertura: 0=SAIH TR · 1=diario · 2=inventario · 3=externo */
  nivelCobertura?: number;
  // F7.2 — SAIH tiempo real (cada 15 min)
  /** Caudal del río en la estación (m³/s) — SAIH */
  caudalRioM3s?: number;
  /** Caudal desembalsado actual (m³/s) — SAIH */
  caudalDesembalsadoM3s?: number;
  /** Identificador de estación SAIH */
  idEstacionSAIH?: string;
  // F7.3 — zonas de sombra Guadiana/Segura
  /** Enlace externo al SAIH de la demarcación (solo nivelCobertura=3) */
  enlaceSAIHExterno?: string;

  [key: string]: string | number | boolean | undefined;
}

// ─── Candidato de referencia (fuente oficial) ────────────────────────────────

/** Infraestructura procedente de una fuente oficial normalizada a formato común */
export interface CandidatoReferencia {
  /** Identificador en la fuente original (ej: «DERA-SAN-001», «edu-12345») */
  idFuente: string;
  nombre: string;
  tipologia: InfrastructureType;
  fuente: FuenteReferencia;
  /** UTM ETRS89 EPSG:25830 */
  x25830: number;
  y25830: number;
  /** Código INE 5 dígitos del municipio */
  ine5: string;
  /** Dirección postal (opcional, si la fuente la aporta) */
  direccion?: string;
  /**
   * OL-04 — Código de destino catastral DGC (3 letras): 'IQU', 'IEN', 'EGS', 'INA'…
   * Presente cuando el candidato procede de una fuente con datos catastrales
   * o cuando InventarioReferenciaBuilder enriquece con CatastroR2.
   * Eleva la confianza normativa en resolverSubtipoNormativo() para INDUSTRIAL/FUEL.
   */
  destino_cod?: string;
  metadata?: MetadatosEnriquecidos;
}

// ─── Referencia normativa ────────────────────────────────────────────────────

export interface ReferenciaNormativa {
  norma: string;       // Vacía si no hay norma leída que sostenga la cita (NORMA-b)
  articulo: string;    // Solo un artículo leído en el texto oficial; si no, vacío
  criticidad: CriticidadLegal;
  confianza: 'alta' | 'media' | 'baja';
}

// ─── Scoring del matching ─────────────────────────────────────────────────────

/** Desglose del score compuesto (0-100) entre infra PTEL y candidato referencia */
export interface MatchScore {
  total: number;           // 0-100
  nombre: number;          // 0-40  (uFuzzy)
  distancia: number;       // 0-30  (Flatbush, radio por tipología)
  tipologia: number;       // 0-20  (F3.07 classifier)
  catastral: number;       // 0-10  (uso suelo catastral)
  distanciaMetros: number;
}

// ─── Resultados de matching por elemento ────────────────────────────────────

/** Infra PTEL que tiene match confirmado o probable con un candidato de referencia */
export interface MatchConfirmado {
  infraPTEL: ProcessedInfrastructure;
  candidato: CandidatoReferencia;
  score: MatchScore;
  estado: Extract<EstadoCotejo, 'incluido' | 'probable'>;
}

/** Candidato de referencia AUSENTE del documento PTEL */
export interface InfraAusente {
  candidato: CandidatoReferencia;
  estado: Extract<EstadoCotejo, 'ausente_critico' | 'ausente_recomendado'>;
  criticidad: CriticidadLegal;
  referenciaNormativa: ReferenciaNormativa;
}

/** Infra PTEL sin correspondencia en fuentes de referencia (puede ser válida) */
export interface InfraSobrante {
  infraPTEL: ProcessedInfrastructure;
  estado: Extract<EstadoCotejo, 'sobrante'>;
}

// ─── Estadísticas ────────────────────────────────────────────────────────────

/** Estadísticas de completitud por tipología individual */
export interface EstadisticaTipologia {
  tipologia: InfrastructureType;
  totalPTEL: number;
  totalReferencia: number;
  incluidos: number;
  ausentes: number;
  sobrantes: number;
  ratioCobertura: number;  // 0-1
}

/** Estadísticas agregadas del cotejo completo */
export interface EstadisticasCotejo {
  totalPTEL: number;
  totalReferencia: number;
  incluidos: number;
  probables: number;
  ausentesCriticos: number;
  ausentesRecomendados: number;
  sobrantes: number;
  ratioCoberturaGlobal: number;    // 0-1
  porTipologia: EstadisticaTipologia[];
}

// ─── Opciones y resultado completo ──────────────────────────────────────────

export interface OpcionesCotejo {
  tipologias: InfrastructureType[];
  umbralConfirmado?: number;       // Default 60
  umbralProbable?: number;         // Default 40
  incluirSobrantes?: boolean;
  radioMaximoMetros?: number;      // Override radio por tipología
}

export interface ResultadoCotejo {
  ine5: string;
  municipio: string;
  timestamp: string;               // ISO 8601
  matches: MatchConfirmado[];
  ausentes: InfraAusente[];
  sobrantes: InfraSobrante[];
  estadisticas: EstadisticasCotejo;
  opciones: OpcionesCotejo;
  /**
   * C0-d: lo que el técnico decidió en P3 sobre los ausentes. Solo lo trae el
   * cotejo que se pasa a P4 (`aplicarDecisionesAlResultado`); el motor nunca lo
   * rellena. Los ausentes de aquí ya no están en `ausentes`.
   */
  decisionesTecnico?: DecisionesTecnicoCotejo;
}

/** C0-d: un ausente decidido por el técnico en P3, con la fecha de la decisión (ISO 8601). */
export interface AusenteDecidido {
  ausente: InfraAusente;
  fecha: string;
}

/** C0-d: «Añadir al PTEL» → `propuestos`; «Ignorar» → `descartados` (DOP-1 y DOP-2 de `C0-d`). */
export interface DecisionesTecnicoCotejo {
  propuestos: AusenteDecidido[];
  descartados: AusenteDecidido[];
}

// ─── Constantes ──────────────────────────────────────────────────────────────

/** Umbrales de decisión para el motor de matching */
export const MATCH_THRESHOLDS = {
  CONFIRMADO: 60,
  PROBABLE: 40,
} as const;

/** Opciones por defecto: tipologías críticas obligatorias */
export const OPCIONES_COTEJO_DEFAULT: Required<OpcionesCotejo> = {
  tipologias: [],
  umbralConfirmado: MATCH_THRESHOLDS.CONFIRMADO,
  umbralProbable: MATCH_THRESHOLDS.PROBABLE,
  incluirSobrantes: true,
  radioMaximoMetros: 500,
};

// ─── Factories ───────────────────────────────────────────────────────────────

export function crearOpcionesCotejo(parcial?: Partial<OpcionesCotejo>): OpcionesCotejo {
  return { ...OPCIONES_COTEJO_DEFAULT, ...parcial };
}

export function crearCandidatoReferencia(
  parcial: Omit<CandidatoReferencia, 'idFuente'> & { idFuente?: string }
): CandidatoReferencia {
  return {
    idFuente: parcial.idFuente ?? `${parcial.fuente}_${parcial.ine5}_${Date.now()}`,
    ...parcial,
  };
}

// ─── Type guards ─────────────────────────────────────────────────────────────

export function esEstadoCotejo(valor: string): valor is EstadoCotejo {
  return ['incluido', 'probable', 'ausente_critico', 'ausente_recomendado', 'sobrante', 'sin_match']
    .includes(valor);
}

export function esCriticidadLegal(valor: string): valor is CriticidadLegal {
  return ['critica', 'alta', 'media', 'baja'].includes(valor);
}

export function esInfraAusente(item: InfraAusente | MatchConfirmado | InfraSobrante): item is InfraAusente {
  return item.estado === 'ausente_critico' || item.estado === 'ausente_recomendado';
}

export function esMatchConfirmado(item: InfraAusente | MatchConfirmado | InfraSobrante): item is MatchConfirmado {
  return item.estado === 'incluido' || item.estado === 'probable';
}

export function esInfraSobrante(item: InfraAusente | MatchConfirmado | InfraSobrante): item is InfraSobrante {
  return item.estado === 'sobrante';
}
